// Identity step — part number, description, drawing upload zone, notes.
//
// Used as step 1 for machined / cut_blank / weldment / hybrid / collar parts.
// (Purchased parts skip Identity and go straight to Source.)
//
// Drawing zone: the real DrawingUpload widget is rendered whenever the part
// has been saved (i.e. has a stable id). For an unsaved draft (e.g. the
// PartWizardTestPage smoke screen, or in-flight before NewPartModal persists)
// we render a friendly placeholder explaining the user needs to save first —
// uploads are bound to /drawings/parts/{partId}/* in Storage and we can't
// pick the path until that id exists.
//
// On successful upload(s) we merge the returned URLs into part.attachedDrawings
// via the wizard's onChange — the same merge path the rest of the wizard uses.

import { DrawingUpload } from '@/components/DrawingUpload';
import type { StepProps } from '../stepTypes';

export function IdentityStep({ part, onChange }: StepProps) {
  // Real partId? -> real DrawingUpload. Otherwise a "save first" hint.
  // We intentionally key on a truthy id rather than relying on `part.id` typing
  // because Partial<Part> means id is type-optional even in saved drafts.
  const partId = part.id ?? null;

  function handleUploaded(newUrls: string[]) {
    const existing = part.attachedDrawings ?? [];
    onChange({ attachedDrawings: [...existing, ...newUrls] });
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
        <Field label="Part number *" className="sm:col-span-2">
          <input
            type="text"
            value={part.number ?? ''}
            onChange={(e) => onChange({ number: e.target.value })}
            placeholder="BRK-2245-A"
            autoFocus
            className="w-full px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
          />
        </Field>
        <Field label="Description" className="sm:col-span-3">
          <input
            type="text"
            value={part.description ?? ''}
            onChange={(e) => onChange({ description: e.target.value })}
            placeholder="Mounting bracket — Acme conveyor"
            className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
          />
        </Field>
      </div>

      <div>
        <div className="text-[10px] font-semibold text-text-mute uppercase tracking-wide mb-1">
          Drawings (optional — drag/drop or click to pick)
        </div>
        {partId ? (
          <DrawingUpload partId={partId} onUploaded={handleUploaded} />
        ) : (
          <div
            className="border-2 border-dashed border-line-dark rounded p-6 text-center text-xs text-text-mute bg-bg/40"
            title="Drawings are stored under /drawings/parts/{partId}/ — save the part first to attach files."
          >
            <div className="text-2xl mb-1">📎</div>
            <div>Save the part first to attach drawings</div>
            <div className="text-[10px] mt-1 italic">
              (uploads need a saved part id to pin to)
            </div>
          </div>
        )}
        {(part.attachedDrawings?.length ?? 0) > 0 && (
          <div className="text-[11px] text-text-mute mt-1.5">
            {part.attachedDrawings!.length} drawing
            {part.attachedDrawings!.length === 1 ? '' : 's'} attached.
          </div>
        )}
      </div>

      <Field label="Notes">
        <textarea
          value={part.notes ?? ''}
          onChange={(e) => onChange({ notes: e.target.value })}
          rows={2}
          placeholder="Anything the operator should know about this part"
          className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange resize-y"
        />
      </Field>
    </div>
  );
}

interface FieldProps {
  label: string;
  children: React.ReactNode;
  className?: string;
}

function Field({ label, children, className }: FieldProps) {
  return (
    <label className={`block ${className ?? ''}`}>
      <span className="block text-[10px] font-semibold text-text-mute uppercase tracking-wide mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
